import { useEffect, useState } from "react";
import {
  Address,
  beginCell,
  fromNano,
  OpenedContract,
  toNano,
} from "@ton/core";
import {
  CollectionMintParams,
  SimpleNftMaster,
} from "../contracts/SimpleNftMasterV2/tact_SimpleNftMaster";
import { useAsyncInitialize } from "./useAsyncInitialize";
import { useTonClient } from "./useTonCLient";
import { creationTonPrice, masterTonContract } from "../config";
import { useTonConnect } from "./useTonConnect";
import {
  getWrappedRoyaltyParams,
  makeCellForLink,
  UnwrappedRoyaltyParams,
} from "../utils/mint";
import { addCollection } from "../utils/storage";
import { backend } from "@/services/backend";
import { sendGtag } from "@/utils/stats";
import { CollectionDataCache } from "@/types";

const rquestPause = 2101; // in ms

const sleep = (time: number = rquestPause) =>
  new Promise(resolve => setTimeout(resolve, time));

export interface CollectionMintData {
  collectionDataUrl: string;
  itemDataUrl: string;
  item_price: number;
  mint_limit: number;
  owner_address: string;
  royaltyParams: UnwrappedRoyaltyParams;
  mint_time_limit: number;
  is_sbt: boolean;
  enable_whitelist?: boolean;
  user_item_limit: bigint;
}

export function useMasterContract() {
  const { client } = useTonClient();
  const { wallet, sender } = useTonConnect();

  const masterContract = useAsyncInitialize(async () => {
    if (!client || !wallet) return;

    const contract: any = SimpleNftMaster.fromAddress(
      Address.parse(masterTonContract)
    );

    return client.open(contract) as OpenedContract<SimpleNftMaster>;
  }, [client, wallet]);

  return {
    contractAddress: masterContract?.address,
    collectionCreationPrice: creationTonPrice,
    newCollectionData: async () => {
      const index = await masterContract?.getGetNextCollectionIndex();
      await sleep(2101);
      console.log("Found index: ", index);
      const address = await masterContract?.getGetCollectionAddressByIndex(
        index || 0n
      );
      await sleep(2101);
      const strAddress = address?.toString();
      const result = {
        index,
        address: strAddress,
      };
      return result;
    },
    mintCollection: async (data: CollectionMintData) => {
      return new Promise(async (resolve, reject) => {
        if (!wallet) {
          reject(new Error("Wallet not connected"));
          return;
        }
        sendGtag("mint_collection", {
          wallet: sender.address,
        });
        try {
          if (
            data.item_price < 0.05 ||
            (data.enable_whitelist && data.item_price < 0.1)
          ) {
            throw new Error(
              `Min price for item in collection is ${
                data.enable_whitelist ? "0.1 TON with whitelist" : "0.05 TON"
              }`
            );
          }
          const collectionContentCell = makeCellForLink(data.collectionDataUrl);
          const itemContentCell = makeCellForLink(data.itemDataUrl);
          const wrappedRoyaltyParams = getWrappedRoyaltyParams(
            data.royaltyParams
          );
          const index = await masterContract?.getGetNextCollectionIndex();
          await sleep();
          const address = await masterContract?.getGetCollectionAddressByIndex(
            index || 0n
          );
          await sleep();

          const mintCollectionDataObject = {
            $$type: "CollectionMintParams" as "CollectionMintParams",
            queryId: 0n,
            owner_address: Address.parse(data.owner_address),
            collection_content: collectionContentCell,
            nft_individual_content_url: itemContentCell,
            nft_price: toNano(String(data.item_price)),
            mint_limit: BigInt(data.mint_limit),
            mint_time_limit: BigInt(data.mint_time_limit),
            is_sbt: data.is_sbt ? 1n : 0n,
            royalty_params: wrappedRoyaltyParams,
            user_item_limit: data.user_item_limit
              ? BigInt(data.user_item_limit)
              : 0n,
            enable_whitelist: data.enable_whitelist || false,
            enable_profile: !!(
              data.enable_whitelist || data.user_item_limit > 0
            ),
          };

          const result = await masterContract?.send(
            sender,
            {
              value: toNano(creationTonPrice),
            },
            mintCollectionDataObject
          );
          let createdCollectionData: CollectionDataCache | null = null;
          if (address) {
            addCollection(address.toString());
            try {
              createdCollectionData = await backend.getCollectionData(address.toString());
            } catch (e) {
              console.log(e);
            }
            resolve({
              success: true,
              address: address.toString(),
              transaction: result,
              experience: createdCollectionData?.experience || 0
            });
          } else {
            reject(new Error("Failed to get collection address"));
          }
        } catch (e) {
          console.error("Mint collection error:", e);
          reject(e);
        }
      });
    },
    withdraw: async (toAddress: string) => {
      return new Promise((resolve, reject) => {
        if (!wallet) {
          resolve(false);
          return;
        }

        masterContract
          ?.send(
            sender,
            {
              value: toNano("0.25"),
            },
            {
              $$type: "Withdraw",
              to: Address.parse(toAddress),
            }
          )
          .then(result => {
            console.log("Result: ", result);
            resolve(true);
          })
          .catch(e => {
            console.log("Error: ", e);
            resolve(false);
          });
      });
    },
  };
}
